import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Store as StoreIcon, Phone, Mail, MapPin } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", address: "", city: "", postal_code: "", country: "France" };
const Lbl = ({ children }) => <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>;

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/stores").then(({ data }) => setStores(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, phone: s.phone || "", email: s.email || "",
      address: s.address || "", city: s.city || "", postal_code: s.postal_code || "",
      country: s.country || "France",
    });
    setOpen(true);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.city) {
      toast.error("Nom, téléphone, adresse et ville sont requis.");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/stores/${editing.id}`, form);
      else await api.post("/admin/stores", form);
      toast.success(editing ? "Magasin mis à jour" : "Magasin créé");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/stores/${toDelete.id}`);
      toast.success("Magasin supprimé");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setToDelete(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Organisation</p>
          <h1 className="font-serif text-4xl text-ink tracking-tight">Magasins ({stores.length})</h1>
        </div>
        <button data-testid="add-store-btn" onClick={openCreate} className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors">
          <Plus className="h-4 w-4" /> Ajouter
        </button>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{[1, 2, 3].map((i) => <div key={i} className="h-40 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : stores.length === 0 ? (
        <p className="text-graphite">Aucun magasin pour le moment.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {stores.map((s) => (
            <div key={s.id} data-testid={`admin-store-${s.id}`} className="bg-white border border-stone rounded-sm p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <StoreIcon className="h-5 w-5 text-copper shrink-0" strokeWidth={1.5} />
                  <h3 className="font-serif text-xl text-ink">{s.name}</h3>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button data-testid={`edit-store-${s.id}`} onClick={() => openEdit(s)} className="p-2 text-graphite hover:text-ink"><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`delete-store-${s.id}`} onClick={() => setToDelete(s)} className="p-2 text-graphite hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-sm text-graphite">
                <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ash" /> {s.address}, {s.postal_code} {s.city}, {s.country}</p>
                <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 shrink-0 text-ash" /> {s.phone}</p>
                {s.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 shrink-0 text-ash" /> {s.email}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-cream max-w-md" data-testid="store-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">{editing ? "Modifier le magasin" : "Nouveau magasin"}</DialogTitle>
            <DialogDescription className="text-graphite text-sm">Coordonnées affichées en pied de page du site.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Lbl>Nom</Lbl><Input data-testid="store-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="France — La Madeleine" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Lbl>Téléphone</Lbl><Input data-testid="store-phone" type="tel" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="03 20 00 00 00" /></div>
              <div><Lbl>Email (optionnel)</Lbl><Input data-testid="store-email" type="email" value={form.email} onChange={set("email")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="magasin@exemple.fr" /></div>
            </div>
            <div><Lbl>Adresse</Lbl><Input data-testid="store-address" value={form.address} onChange={set("address")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="170 rue du général de Gaulle" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Lbl>Code postal</Lbl><Input data-testid="store-postal-code" value={form.postal_code} onChange={set("postal_code")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="59110" /></div>
              <div className="col-span-2"><Lbl>Ville</Lbl><Input data-testid="store-city" value={form.city} onChange={set("city")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="La Madeleine" /></div>
            </div>
            <div><Lbl>Pays</Lbl><Input data-testid="store-country" value={form.country} onChange={set("country")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="France" /></div>
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="save-store-btn" className="w-full bg-ink text-cream py-3.5 uppercase tracking-[0.15em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
                {saving ? "Enregistrement..." : editing ? "Mettre à jour" : "Créer"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-cream">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-ink">Supprimer ce magasin ?</AlertDialogTitle>
            <AlertDialogDescription>« {toDelete?.name} » sera retiré du pied de page du site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-store-btn" onClick={confirmDelete} className="rounded-sm bg-destructive hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
